import { ref, computed } from 'vue';

export interface UserFormData {
  email: string;
  full_name: string;
  password: string;
  role: 'admin' | 'staff' | 'customer';
  staff_type: 'waiter' | 'cashier' | 'kitchen' | 'general' | null;
  is_active: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
}

export function useUserFormValidation(mode: 'create' | 'edit' = 'create') {
  // Error states
  const fullNameError = ref('');
  const fullNameTouched = ref(false);
  const emailError = ref('');
  const emailTouched = ref(false);
  const passwordErrors = ref<string[]>([]);
  const passwordTouched = ref(false);

  // Computed validation states
  const hasErrors = computed(() => {
    return (
      (fullNameError.value && fullNameTouched.value) ||
      (emailError.value && emailTouched.value) ||
      (passwordErrors.value.length > 0 && passwordTouched.value)
    );
  });

  const isValid = computed(() => {
    return !hasErrors.value && fullNameTouched.value && emailTouched.value;
  });

  // Validation functions
  function validateFullName(fullName: string): boolean {
    if (!fullName.trim()) {
      fullNameError.value = 'El nombre completo es obligatorio';
      return false;
    }
    
    if (fullName.trim().length < 2) {
      fullNameError.value = 'El nombre debe tener al menos 2 caracteres';
      return false;
    }
    
    fullNameError.value = '';
    return true;
  }

  function validateEmail(email: string): boolean {
    if (!email.trim()) {
      emailError.value = 'El correo electrónico es obligatorio';
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      emailError.value = 'Formato de correo electrónico inválido (ejemplo: usuario@dominio.com)';
      return false;
    }
    
    // Check for common typos
    const domain = email.split('@')[1];
    if (domain && !domain.includes('.')) {
      emailError.value = 'El dominio debe incluir una extensión (ejemplo: .com, .mx, .net)';
      return false;
    }
    
    emailError.value = '';
    return true;
  }

  function validatePassword(password: string): boolean {
    const errors: string[] = [];
    
    // Only validate if password is provided (required for create, optional for edit)
    if (!password && mode === 'create') {
      errors.push('La contraseña es obligatoria');
      passwordErrors.value = errors;
      return false;
    }
    
    // Skip validation if password is empty in edit mode
    if (!password && mode === 'edit') {
      passwordErrors.value = [];
      return true;
    }
    
    // Length check
    if (password.length < 8) {
      errors.push('Debe tener al menos 8 caracteres');
    }
    
    // Uppercase letter check
    if (!/[A-Z]/.test(password)) {
      errors.push('Debe contener al menos una letra mayúscula');
    }
    
    // Lowercase letter check
    if (!/[a-z]/.test(password)) {
      errors.push('Debe contener al menos una letra minúscula');
    }
    
    // Digit check
    if (!/\d/.test(password)) {
      errors.push('Debe contener al menos un número');
    }
    
    // Special character check
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Debe contener al menos un carácter especial (!@#$%^&*(),.?":{}|<>)');
    }
    
    passwordErrors.value = errors;
    return errors.length === 0;
  }

  function validateForm(formData: UserFormData): ValidationResult {
    const errors: Record<string, string[]> = {};
    
    // Validate all fields
    const isFullNameValid = validateFullName(formData.full_name);
    const isEmailValid = validateEmail(formData.email);
    const isPasswordValid = validatePassword(formData.password);
    
    // Mark all fields as touched
    fullNameTouched.value = true;
    emailTouched.value = true;
    passwordTouched.value = true;
    
    // Collect errors
    if (!isFullNameValid && fullNameError.value) {
      errors.full_name = [fullNameError.value];
    }
    
    if (!isEmailValid && emailError.value) {
      errors.email = [emailError.value];
    }
    
    if (!isPasswordValid && passwordErrors.value.length > 0) {
      errors.password = passwordErrors.value;
    }
    
    const isValid = isFullNameValid && isEmailValid && isPasswordValid;
    
    return {
      isValid,
      errors
    };
  }

  function resetValidation() {
    fullNameError.value = '';
    fullNameTouched.value = false;
    emailError.value = '';
    emailTouched.value = false;
    passwordErrors.value = [];
    passwordTouched.value = false;
  }

  function setBackendErrors(validationErrors: Array<{ field: string; message: string }>) {
    // Clear previous errors
    resetValidation();
    
    // Map backend field names to frontend error refs
    validationErrors.forEach((err) => {
      const field = err.field.replace('body.', ''); // Remove 'body.' prefix
      
      switch (field) {
        case 'full_name':
          fullNameError.value = err.message;
          fullNameTouched.value = true;
          break;
        case 'email':
          emailError.value = err.message;
          emailTouched.value = true;
          break;
        case 'password':
          passwordErrors.value = [err.message];
          passwordTouched.value = true;
          break;
      }
    });
  }

  return {
    // Error states
    fullNameError,
    fullNameTouched,
    emailError,
    emailTouched,
    passwordErrors,
    passwordTouched,
    
    // Computed states
    hasErrors,
    isValid,
    
    // Validation functions
    validateFullName,
    validateEmail,
    validatePassword,
    validateForm,
    resetValidation,
    setBackendErrors
  };
}
